import React, { useState } from "react";
import { Mail, ShoppingBag, Send, Check, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import API from "../api";
import { useToast } from "../hooks/use-toast";
import { WhatsAppIcon, WHATSAPP_URL } from "../components/WhatsAppButton";

const CONTACT_EMAIL = "contact@amourduluxe.com";

const Contact = () => {
  const { t } = useApp();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post("/contact", {
        name: form.name, email: form.email,
        subject: form.subject || null, message: form.message,
        website: form.website || null,
      });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
      toast({ title: t("contactSent") });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec de l'envoi." });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-white border border-border px-4 py-3.5 text-sm outline-none focus:border-[#b08d57] transition-colors placeholder:text-muted-foreground rounded-sm";

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 fade-up">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-[#b08d57]">{t("home")}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-[#1b1b1b]">{t("contactNav")}</span>
      </div>

      <div className="h-px w-10 bg-[#b08d57] mb-4" />
      <h1 className="font-serif-lux text-4xl md:text-5xl text-[#1b1b1b]">{t("contactTitle")}</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-xl leading-relaxed">{t("contactSubtitle")}</p>

      <div className="grid md:grid-cols-[1fr_360px] gap-10 mt-12">
        {/* Form */}
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
          <input aria-hidden="true" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input required data-testid="contact-name" className={inputCls} placeholder={t("contactName")} value={form.name} onChange={set("name")} />
            <input required type="email" data-testid="contact-email" className={inputCls} placeholder={t("contactEmail")} value={form.email} onChange={set("email")} />
          </div>
          <input data-testid="contact-subject" className={inputCls} placeholder={t("contactSubject")} value={form.subject} onChange={set("subject")} />
          <textarea required data-testid="contact-message" rows={7} className={inputCls} placeholder={t("contactMessage")} value={form.message} onChange={set("message")} />
          <button type="submit" disabled={sending} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#1b1b1b] text-white px-8 py-3.5 text-sm tracking-[0.1em] uppercase hover:bg-[#b08d57] transition-colors disabled:opacity-60">
            {sent ? <Check className="h-4 w-4" strokeWidth={2} /> : <Send className="h-4 w-4" strokeWidth={1.5} />} {t("contactSend")}
          </button>
        </form>

        {/* Info */}
        <aside className="bg-[#faf8f5] border border-border rounded-sm p-7 h-fit">
          <h2 className="font-serif-lux text-2xl text-[#1b1b1b] mb-6">{t("contactInfoTitle")}</h2>
          <div className="space-y-6 text-sm">
            <div className="flex gap-3">
              <Mail className="h-5 w-5 text-[#b08d57] shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t("contactEmailLabel")}</p>
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="text-[#1b1b1b] hover:text-[#b08d57] transition-colors">{CONTACT_EMAIL}</a>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[#25D366] shrink-0 mt-0.5"><WhatsAppIcon className="h-5 w-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t("contactWhatsappLabel")}</p>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="text-[#1b1b1b] hover:text-[#25D366] transition-colors">{t("contactWhatsappText")}</a>
              </div>
            </div>
            <div className="flex gap-3">
              <ShoppingBag className="h-5 w-5 text-[#b08d57] shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t("contactOnlineTitle")}</p>
                <p className="text-[#1b1b1b]">{t("contactOnlineText")}</p>
                <p className="text-muted-foreground mt-1">{t("contactResponse")}</p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Contact;
